import * as React from 'react'
import { cva, type VariantProps } from 'class-variance-authority'

import { cn } from '@/lib/cn'
import { Spotlight } from '@/components/aceternity/spotlight'

const cardVariants = cva(
  'bg-paper text-ink flex flex-col gap-6 border border-rule rounded-sm',
  {
    variants: {
      variant: {
        default: '',
        vellum: 'bg-vellum',
        elevated: 'shadow-sm',
      },
      padding: {
        none: '',         // current default — no breaking change
        compact: 'p-4',
        default: 'p-5',
        loose: 'p-6',
        spacious: 'p-7',
      },
    },
    defaultVariants: {
      variant: 'default',
      padding: 'none',
    },
  },
)

function Card({
  className,
  variant,
  padding,
  ...props
}: React.ComponentProps<'div'> & VariantProps<typeof cardVariants>) {
  return (
    <div
      data-slot="card"
      className={cn(cardVariants({ variant, padding }), className)}
      {...props}
    />
  )
}

function CardHeader({ className, ...props }: React.ComponentProps<'div'>) {
  return (
    <div
      data-slot="card-header"
      className={cn(
        '@container/card-header grid auto-rows-min grid-rows-[auto_auto] items-start gap-2 px-6 py-4 has-data-[slot=card-action]:grid-cols-[1fr_auto] [.border-b]:pb-4',
        className,
      )}
      {...props}
    />
  )
}

function CardTitle({ className, ...props }: React.ComponentProps<'div'>) {
  return (
    <div
      data-slot="card-title"
      className={cn('font-sans text-lg font-semibold text-ink leading-none text-balance', className)}
      {...props}
    />
  )
}

function CardDescription({ className, ...props }: React.ComponentProps<'div'>) {
  return (
    <div
      data-slot="card-description"
      className={cn('font-sans text-sm text-graphite text-pretty', className)}
      {...props}
    />
  )
}

function CardAction({ className, ...props }: React.ComponentProps<'div'>) {
  return (
    <div
      data-slot="card-action"
      className={cn(
        'col-start-2 row-span-2 row-start-1 self-start justify-self-end',
        className,
      )}
      {...props}
    />
  )
}

function CardContent({ className, ...props }: React.ComponentProps<'div'>) {
  return (
    <div
      data-slot="card-content"
      className={cn('px-6 py-4', className)}
      {...props}
    />
  )
}

function CardFooter({ className, ...props }: React.ComponentProps<'div'>) {
  return (
    <div
      data-slot="card-footer"
      className={cn('flex items-center px-6 py-4 [.border-t]:pt-4', className)}
      {...props}
    />
  )
}

/**
 * SpotlightCard — a Card variant with a cursor-following glow effect on hover.
 *
 * Uses Spotlight as the outer container so mouse-tracking listeners wrap the
 * entire card. Card styles are applied to Spotlight's wrapper div via className.
 * Children are rendered in a `relative z-20` wrapper so they paint above the
 * glow overlay (which lives at z-10 inside Spotlight).
 */
function SpotlightCard({ className, children, ...props }: React.ComponentProps<'div'>) {
  return (
    <Spotlight
      className={cn(
        'bg-paper text-ink flex flex-col gap-6 border border-rule rounded-sm p-6',
        className,
      )}
    >
      <div className="relative z-20 flex flex-col gap-6" {...props}>
        {children}
      </div>
    </Spotlight>
  )
}

export {
  Card,
  cardVariants,
  CardHeader,
  CardFooter,
  CardTitle,
  CardAction,
  CardDescription,
  CardContent,
  SpotlightCard,
}
